import { useCallback } from "react";
import { IntegrationCoaMapping } from "@common/schemas/domains/accounting/integrationCoaMapping.schema";
import { Task, TaskStatus } from "@common/types/task";
import {
    useIntegrationCoaMappings,
    useIsUpdatingIntegrationCoaMapping,
} from "@api/integrationCoaMapping.api";
import { useUpdateTask } from "@api/task.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import {
    countUnmappedMappings,
    getIntegrationCoaMappingTaskScope,
} from "./integrationCoaMappingTask";

interface Props {
    selectedTask: Task;
}

function getMarkAsDoneBlockedReason({
    mappings,
    isUpdatingMapping,
}: {
    mappings: IntegrationCoaMapping[] | undefined;
    isUpdatingMapping: boolean;
}): string {
    if (!mappings) {
        return "Couldn't load transaction types";
    }
    if (mappings.length === 0) {
        return "No transaction types imported yet";
    }
    if (countUnmappedMappings(mappings) > 0) {
        return "Choose an account for every transaction type first";
    }
    if (isUpdatingMapping) {
        return "Saving account mapping";
    }
    return "";
}

const MarkAsDoneAction = ({ selectedTask }: Props) => {
    const { entityId, provider } =
        getIntegrationCoaMappingTaskScope(selectedTask);

    const { data: mappings, isLoading } = useIntegrationCoaMappings({
        entityId,
        provider,
    });
    const isUpdatingMapping = useIsUpdatingIntegrationCoaMapping({
        entityId,
        provider,
    });
    const updateTask = useUpdateTask(selectedTask);
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const blockedReason = getMarkAsDoneBlockedReason({
        mappings,
        isUpdatingMapping,
    });
    const tooltip = useViewOnlyTooltipReason(blockedReason);

    const handleClick = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        updateTask.mutate({ status: TaskStatus.DONE });
    }, [refuseIfViewOnly, updateTask]);

    if (isLoading) {
        return null;
    }

    return (
        <Tooltip content={tooltip}>
            <ButtonWithLoader
                loading={updateTask.isPending}
                variant="primary"
                size="md"
                disabled={blockedReason !== ""}
                onClick={handleClick}
            >
                Mark as done
            </ButtonWithLoader>
        </Tooltip>
    );
};

export const IntegrationCoaMappingCommandIsland = ({ selectedTask }: Props) => (
    <CommandIsland alignEnd className="mx-3" sticky>
        <MarkAsDoneAction selectedTask={selectedTask} />
    </CommandIsland>
);
